import React, { useCallback, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import {
  getDashboardMetricsPreSaleEndpoint,
  getDashboardMetricsV2Endpoint,
  getDashboardPreSaleVisitsProperEndpoint,
  getDashboardVisitsProperEndpoint,
} from '../services/dashboardService';
import useAdminEntity from '../../../hooks/useAdminEntity';
import {
  PeriodMetrics,
  ProperPreSaleMetrics,
  PropersAssignedPreSaleVisits,
  PropersAssignedVisits,
  ProperV2Metrics,
  StatsMetrics,
} from '../models/dashboardModels';
import { Col, Container, Row } from 'reactstrap';
import useAdminPagination from '../../../hooks/useAdminPagination';
import AdminPaginator from '../../../components/admin/AdminPaginator';
import DashboardVisitManagerListTable from './DashboardVisitManagerListTable';
import DashboardPreSaleVisitManagerListTable from './DashboardPreSaleVisitManagerListTable';
import { activeTab } from '../types/dashboard.types';
import NavigationTabDashboard from './NavigationTabDashboard';
import MetricsDashboard from './MetricsDashboard';
import {
  VISIT_STATUS,
  VisitPreSaleState,
} from '../../../constants/visitsConstants';
import MetricsDateDashboard from './MetricsDateDashboard';
import useUserCountry from '../../../hooks/useUserCountry';

const InternalUserDashboard: React.FC = () => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { getCurrentCountry } = useUserCountry();
  const currenUserCountryCode = getCurrentCountry()?.code || '';

  const [activeTab, setActiveTab] = useState<activeTab>(
    currenUserCountryCode === 'SV' ? 'rent-secondary' : 'pre-sale',
  );

  const defaultPeriod: PeriodMetrics = {
    end: '',
    start: '',
  };

  const getDashboardMetrics = useCallback(
    () => getDashboardMetricsV2Endpoint(token, 'internal-user'),
    [token],
  );

  const [dashboardMetrics] = useAdminEntity<ProperV2Metrics>(
    getDashboardMetrics,
    console.error,
  );

  const getDashboardPreSaleMetrics = useCallback(
    () => getDashboardMetricsPreSaleEndpoint(token, 'internal-user'),
    [token],
  );

  const [dashboardPreSaleMetrics] = useAdminEntity<ProperPreSaleMetrics>(
    getDashboardPreSaleMetrics,
    console.error,
  );

  const [dashboardVisits, setDashboardVisits] = useState<
    PropersAssignedVisits[]
  >([]);
  const { paginator, loadItems: dashboardVisitLoadItems } = useAdminPagination(
    getDashboardVisitsProperEndpoint,
    setDashboardVisits,
    console.error,
  );

  const [dashboardPreSaleVisits, setDashboardPreSaleVisits] = useState<
    PropersAssignedPreSaleVisits[]
  >([]);
  const {
    paginator: paginatorPreSaleVisits,
    loadItems: dashboardPreSaleVisitLoadItems,
  } = useAdminPagination(
    getDashboardPreSaleVisitsProperEndpoint,
    setDashboardPreSaleVisits,
    console.error,
  );

  if (!dashboardMetrics || !dashboardPreSaleMetrics) {
    return <>Cargando</>;
  }

  const rentalInfo: StatsMetrics = {
    pending_approval:
      dashboardMetrics?.visits.find(
        (f) => f.status === VISIT_STATUS.PENDING_APPROVAL,
      )?.counter ?? 0,
    done:
      dashboardMetrics?.visits.find((f) => f.status === VISIT_STATUS.DONE)
        ?.counter ?? 0,
    cancelled:
      dashboardMetrics?.visits.find((f) => f.status === VISIT_STATUS.CANCELLED)
        ?.counter ?? 0,
    rescheduled:
      dashboardMetrics?.visits.find(
        (f) => f.status === VISIT_STATUS.RESCHEDULED,
      )?.counter ?? 0,
    scheduled:
      dashboardMetrics?.visits.find((f) => f.status === VISIT_STATUS.SCHEDULED)
        ?.counter ?? 0,
    total:
      dashboardMetrics?.visits.reduce((sum, v) => {
        return sum + +v.counter;
      }, 0) ?? 0,
  };

  const preSaleInfo: StatsMetrics = {
    pending_approval:
      dashboardPreSaleMetrics?.visits.find(
        (f) => f.status === VisitPreSaleState.PENDING_APPROVAL,
      )?.counter ?? 0,
    done:
      dashboardPreSaleMetrics?.visits.find(
        (f) => f.status === VisitPreSaleState.DONE,
      )?.counter ?? 0,
    cancelled:
      dashboardPreSaleMetrics?.visits.find(
        (f) => f.status === VisitPreSaleState.DELETED,
      )?.counter ?? 0,
    rescheduled:
      dashboardPreSaleMetrics?.visits.find(
        (f) => f.status === VisitPreSaleState.RESCHEDULED,
      )?.counter ?? 0,
    scheduled:
      dashboardPreSaleMetrics?.visits.find(
        (f) => f.status === VisitPreSaleState.SCHEDULED,
      )?.counter ?? 0,
    total:
      dashboardPreSaleMetrics?.visits.reduce((sum, v) => {
        return sum + +v.counter;
      }, 0) ?? 0,
  };

  return (
    <Container className="mt-6" fluid>
      {/* Navigation Tabs Component */}
      <NavigationTabDashboard
        activeTab={activeTab}
        onTabChange={setActiveTab}
        preSaleCount={preSaleInfo.total}
        rentalSecondaryCount={rentalInfo.total}
      />

      <MetricsDateDashboard
        activeTab={activeTab}
        period={
          activeTab === 'rent-secondary'
            ? (dashboardMetrics?.period ?? defaultPeriod)
            : (dashboardPreSaleMetrics?.period ?? defaultPeriod)
        }
      />

      <MetricsDashboard
        activeTab={activeTab}
        stats={activeTab === 'rent-secondary' ? rentalInfo : preSaleInfo}
      />
      {activeTab === 'rent-secondary' && (
        <Row>
          <Col sm={12}>
            {dashboardVisits && dashboardVisits.length > 0 && (
              <DashboardVisitManagerListTable
                visits={dashboardVisits}
                footer={
                  <AdminPaginator
                    page={paginator.page}
                    totalPages={paginator.pageCount}
                    onPageChange={dashboardVisitLoadItems}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}
      {activeTab === 'pre-sale' && (
        <Row>
          <Col sm={12}>
            {dashboardPreSaleVisits && dashboardPreSaleVisits.length > 0 && (
              <DashboardPreSaleVisitManagerListTable
                visits={dashboardPreSaleVisits}
                footer={
                  <AdminPaginator
                    page={paginatorPreSaleVisits.page}
                    totalPages={paginatorPreSaleVisits.pageCount}
                    onPageChange={dashboardPreSaleVisitLoadItems}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default InternalUserDashboard;
